import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import { getFdeContent } from '../../content/fdeContent'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import {
  LuUsers, LuTarget, LuCode, LuTrendingUp, LuDatabase, LuServer,
  LuCheck, LuLightbulb, LuPuzzle, LuBot, LuSparkles, LuFilter, LuLifeBuoy,
  LuBookOpen, LuClock, LuSearch, LuBox, LuMonitor,
  LuArrowRight, LuWorkflow, LuCloud, LuActivity,
} from 'react-icons/lu'

/*
 * Forward Deployed Engineering, Lean it 101.  Brief-pinned world: the FDE deck
 * (operations control-room / systems-HUD).  Rebuilt to the deck.
 *
 * THESIS: the page is the deck alive, a dark control room where each section is
 *   a live HUD diagram that DEMONSTRATES how embedded senior engineers turn
 *   operational complexity into reliable production software.  Refuses the
 *   card-grid + eyebrow-per-section + 01/02/03 arrangement.
 * OWN-WORLD: deep navy field, one electric blue (#19a2cf) carrying it, glowing
 *   SVG connectors between labelled nodes and HUD rings as the primary material;
 *   rose reserved strictly for the "before". Quicksand display, solid two-tone.
 * STORY: an ops/eng leader sees the pilot was the easy part, that FDE embeds
 *   senior builders who own it end to end (backed by Benjamin, proven at Vale /
 *   Softplan / Segware), and brings us one painful workflow.
 * FIRST VIEWPORT: a full-width HUD flow (complexity -> embedded engineers ->
 *   reliable production) animating its connectors, headline to the left.
 * FORM: the deck's systems-diagram language, committed natively; pinned brief.
 */

const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null)
  const [state, setState] = useState('shown')
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    if (el.getBoundingClientRect().top <= window.innerHeight * 0.86) return undefined
    setState('hidden')
    const io = new IntersectionObserver(
      (e) => { if (e[0] && e[0].isIntersecting) { setState('in'); io.disconnect() } },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' }
    )
    io.observe(el)
    const t = setTimeout(() => { setState('in'); io.disconnect() }, 2400)
    return () => { io.disconnect(); clearTimeout(t) }
  }, [])
  return <div ref={ref} className={`rv ${state === 'hidden' ? 'rv-off' : 'rv-on'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>
}

const H = ({ lead, hot, className = '' }) => (
  <h2 className={`font-Quicksand text-[2.1rem] font-light leading-[1.06] tracking-[-0.03em] text-white sm:text-[2.7rem] md:text-[3.1rem] ${className}`}>
    {lead}{hot ? <> <span className="text-[#4cbfe0]">{hot}</span></> : null}
  </h2>
)

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const useInViewOnce = (threshold = 0.4) => {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined }
    const io = new IntersectionObserver((e) => { if (e[0] && e[0].isIntersecting) { setSeen(true); io.disconnect() } }, { threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [threshold])
  return [ref, seen]
}

// Counts a leading number up when scrolled into view; passes non-numeric values
// through. The real value is the initial render, so prerendered HTML, crawlers
// and reduced-motion readers always see R$50M+, never a zero; the count-up is
// purely an enhancement layered on top.
const Metric = ({ value, className = '' }) => {
  const parts = String(value).match(/^(\D*)(\d[\d.,]*)(.*)$/)
  const target = parts ? parseFloat(parts[2].replace(/,/g, '')) : null
  const [ref, seen] = useInViewOnce(0.5)
  const [n, setN] = useState(target)
  useEffect(() => {
    if (!seen || target === null || prefersReducedMotion()) return undefined
    let raf; const start = performance.now(); const dur = 1300
    const tick = (now) => {
      const t = Math.min((now - start) / dur, 1)
      setN(target * (1 - Math.pow(1 - t, 3)))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [seen, target])
  if (target === null) return <span className={className}>{value}</span>
  const shown = Number.isInteger(target) ? Math.round(n) : n.toFixed(1)
  return <span ref={ref} className={className}>{parts[1]}{shown}{parts[3]}</span>
}

const scrollToId = (id) => { const el = typeof document !== 'undefined' && document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
const CONTACT_API = (process.env.REACT_APP_API_BASE_URL || 'https://app.leanit101.com')
  .replace(/\/$/, '')
  // plain http to the API dies in CORS preflight (nginx 301s to https)
  .replace(/^http:\/\/(?=[^/]*leanit101\.com)/, 'https://')
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

// ── data ─────────────────────────────────────────────────────────────────────
// Icon sets; every label comes from fdeContent so the page speaks three languages.
const COMBINE_ICONS = [LuLightbulb, LuCode, LuDatabase, LuWorkflow, LuPuzzle, LuTarget]
const VALUE_ICONS = [LuBot, LuWorkflow, LuServer, LuSparkles, LuPuzzle, LuFilter, LuMonitor, LuLifeBuoy]
const ENGAGEMENT_ICONS = [LuSearch, LuBox, LuActivity, LuBookOpen]
const PAIN_ICONS = [LuClock, LuActivity, LuTarget, LuWorkflow, LuPuzzle]
const LEAN_CHIP_ICONS = [LuTarget, LuCode, LuCloud, LuTrendingUp]

// ── reusable SVG glow defs ───────────────────────────────────────────────────
const Defs = () => (
  <defs>
    <filter id="fg" x="-40%" y="-40%" width="180%" height="180%">
      <feGaussianBlur stdDeviation="3.2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    <linearGradient id="fline" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stopColor="#0d4a5e" /><stop offset="0.5" stopColor="#19a2cf" /><stop offset="1" stopColor="#4cbfe0" />
    </linearGradient>
    <radialGradient id="fnode" cx="0.5" cy="0.42" r="0.6">
      <stop offset="0" stopColor="#0d3a4e" /><stop offset="1" stopColor="#04141a" />
    </radialGradient>
  </defs>
)

// ── contact form ─────────────────────────────────────────────────────────────
const ContactForm = ({ f }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' })
  const [touched, setTouched] = useState({})
  // honeypot: humans never see it, bots fill it, the server drops those
  const [trap, setTrap] = useState('')
  const [status, setStatus] = useState('idle')
  const errors = {}
  if (form.name.trim().length < 2) errors.name = f.errName
  if (!isEmail(form.email.trim())) errors.email = f.errEmail
  if (form.message.trim().length < 10) errors.message = f.errMessage
  const set = (f) => (e) => { const v = e.target.value; setForm((p) => ({ ...p, [f]: v })); if (status === 'error') setStatus('idle') }
  const blur = (f) => () => setTouched((p) => ({ ...p, [f]: true }))
  const showErr = (f) => (touched[f] || status === 'error') && errors[f]
  const inputCls = (f) => `w-full rounded-lg border bg-[#06141c] px-4 py-3 text-white outline-none transition placeholder:text-[#4d6488] ${showErr(f) ? 'border-rose-400/70' : 'border-[#1c3255] focus:border-[#19a2cf]'}`
  const submit = async (e) => {
    e.preventDefault(); setTouched({ name: true, email: true, company: true, message: true })
    if (Object.keys(errors).length) { setStatus('error'); return }
    setStatus('sending'); trackEvent('fde_contact_submit_attempt')
    const name = form.name.trim(); const email = form.email.trim(); const company = form.company.trim(); const message = form.message.trim()
    const payload = { website: trap, name, fullName: name, email, workEmail: email, company, companyName: company, message, notes: message, source: 'forward-deployed-engineering', formData: { name, email, company, message, source: 'forward-deployed-engineering' }, contact: { name, email, company, message } }
    try {
      const res = await fetch(`${CONTACT_API}/api/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      if (!res.ok) throw new Error('x'); setStatus('sent'); trackEvent('fde_contact_submit_success')
    } catch (err) { setStatus('error'); trackEvent('fde_contact_submit_error') }
  }
  if (status === 'sent') return (
    <div className="mx-auto max-w-lg rounded-2xl border border-[#19a2cf]/25 bg-[#19a2cf]/[0.05] p-8 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[#19a2cf]/50 bg-[#19a2cf]/10"><LuCheck className="text-xl text-[#4cbfe0]" /></div>
      <h3 className="font-Quicksand text-xl font-bold text-white">{f.successTitle}</h3>
      <p className="mt-2 text-[#9db4d0]">{f.successBody}</p>
    </div>
  )
  return (
    <form onSubmit={submit} noValidate className="mx-auto grid max-w-lg gap-3.5 text-left">
      <input type="text" name="website" value={trap} onChange={(e) => setTrap(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />
      <div className="grid gap-3.5 sm:grid-cols-2">
        <label className="text-sm"><span className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6f88ad]">{f.name}</span><input className={inputCls('name')} value={form.name} onChange={set('name')} onBlur={blur('name')} placeholder={f.namePh} />{showErr('name') && <span className="mt-1.5 block text-sm text-rose-300">{errors.name}</span>}</label>
        <label className="text-sm"><span className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6f88ad]">{f.email}</span><input className={inputCls('email')} value={form.email} onChange={set('email')} onBlur={blur('email')} placeholder={f.emailPh} />{showErr('email') && <span className="mt-1.5 block text-sm text-rose-300">{errors.email}</span>}</label>
      </div>
      <label className="text-sm"><span className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6f88ad]">{f.company} <span className="text-white/25">{f.optional}</span></span><input className={inputCls('company')} value={form.company} onChange={set('company')} placeholder={f.companyPh} /></label>
      <label className="text-sm"><span className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6f88ad]">{f.message}</span><textarea rows={4} className={inputCls('message')} value={form.message} onChange={set('message')} onBlur={blur('message')} placeholder={f.messagePh} />{showErr('message') && <span className="mt-1.5 block text-sm text-rose-300">{errors.message}</span>}</label>
      <div className="flex flex-col items-center gap-3 pt-1">
        <button type="submit" disabled={status === 'sending'} className="btn-hot w-full sm:w-auto">{status === 'sending' ? f.sending : f.submit}</button>
        {status === 'error' && <p className="text-sm text-rose-300">{Object.keys(errors).length ? f.errCheck : f.errSend}</p>}
        <p className="text-xs text-[#7089b0]">{f.footNote}</p>
      </div>
    </form>
  )
}

// ── page ─────────────────────────────────────────────────────────────────────
const ForwardDeployedEngineeringPage = () => {
  const { i18n } = useTranslation()
  const c = getFdeContent(i18n.language)
  return (
    <div className="relative isolate w-full overflow-hidden bg-transparent font-Barlow text-[#c3d3e8]">
      <Seo
        title={c.seo.title}
        description={c.seo.description}
        path="/forward-deployed-engineering"
        image="/images/og-leanit101-v2.jpg"
      />
      <style>{`
        .rv { transition: opacity .8s cubic-bezier(.16,.84,.28,1), transform .8s cubic-bezier(.16,.84,.28,1); }
        .rv-off { opacity:0; transform: translateY(30px); }
        .rv-on { opacity:1; transform:none; }
        .grad { background:linear-gradient(92deg,#c9d33a 0%,#f88116 46%,#19a2cf 100%); -webkit-background-clip:text; background-clip:text; color:transparent; }
        .tok { fill:#f88116; filter: drop-shadow(0 0 2.5px rgba(248,129,22,.95)); }
        .btn-hot { display:inline-flex; align-items:center; justify-content:center; gap:.5rem; border-radius:10px; padding:.85rem 1.7rem; font-weight:700; color:#13131f; background:linear-gradient(90deg,#c9d33a,#f88116,#19a2cf); box-shadow:0 16px 40px -16px rgba(248,129,22,.5); transition:transform .25s ease, box-shadow .25s ease; }
        .btn-hot:hover { transform:translateY(-2px); box-shadow:0 20px 50px -14px rgba(25,162,207,.9); }
        .btn-ghost { display:inline-flex; align-items:center; justify-content:center; gap:.5rem; border-radius:10px; padding:.85rem 1.7rem; font-weight:600; color:#dbe8fb; border:1px solid rgba(25,162,207,.28); background:rgba(25,162,207,.05); transition:transform .25s ease, border-color .25s ease; }
        .btn-ghost:hover { transform:translateY(-2px); border-color:rgba(25,162,207,.6); }
        .flow { stroke:url(#fline); fill:none; stroke-linecap:round; filter:url(#fg); stroke-dasharray:5 9; animation: dash 2.4s linear infinite; }
        .flow-r { stroke:#7f2436; fill:none; stroke-linecap:round; stroke-dasharray:4 8; animation: dash 3s linear infinite; }
        @keyframes dash { to { stroke-dashoffset:-140; } }
        .pulse { animation: pl 3.4s ease-in-out infinite; }
        @keyframes pl { 0%,100%{ box-shadow:0 6px 30px -14px rgba(25,162,207,.6), inset 0 0 22px -12px rgba(25,162,207,.55);} 50%{ box-shadow:0 10px 52px -12px rgba(25,162,207,.85), inset 0 0 26px -10px rgba(25,162,207,.8);} }
        .hair { border-top:1px solid rgba(25,162,207,.13); }
        /* a live activation pulse that travels through a sequence of steps */
        .seq { animation: seqp 6.4s ease-in-out infinite; }
        @keyframes seqp { 0%,18%,100%{ transform:scale(1); border-color:rgba(25,162,207,.55); box-shadow:0 6px 30px -14px rgba(25,162,207,.5);} 5%{ transform:scale(1.12); border-color:rgba(248,129,22,.9); box-shadow:0 0 26px -3px rgba(248,129,22,.78);} }
        .rail-dot { position:absolute; top:22px; left:0; height:10px; width:10px; border-radius:9999px; background:#f88116; box-shadow:0 0 12px 2px rgba(248,129,22,.85); animation: railmove 6.4s ease-in-out infinite; }
        @keyframes railmove { 0%{ left:2%; opacity:0;} 7%{ opacity:1;} 93%{ opacity:1;} 100%{ left:98%; opacity:0;} }
        .stg-rect { animation: stgr 6.4s ease-in-out infinite; }
        @keyframes stgr { 0%,20%,100%{ stroke:rgba(25,162,207,.3);} 6%{ stroke:#f88116;} }
        .stg-dot { animation: stgd 6.4s ease-in-out infinite; }
        @keyframes stgd { 0%,20%,100%{ fill:#2aa7cf;} 6%{ fill:#f88116;} }
        .lift { transition: transform .3s ease, border-color .3s ease, background-color .3s ease; }
        .lift:hover { transform: translateY(-3px); }
        @media (prefers-reduced-motion: reduce){ .rv{opacity:1!important;transform:none!important;transition:none!important;} .flow,.flow-r,.pulse,.tok,.seq,.rail-dot,.stg-rect,.stg-dot{animation:none;} }
      `}</style>

      {/* shared site-wide ambient field */}
      <SiteAmbient />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative px-5 pt-32 pb-14 md:pt-40">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#2aa7cf]">{c.hero.kicker}</p>
            <h1 className="mt-6 max-w-4xl font-Quicksand text-[2.9rem] font-light leading-[1.02] tracking-[-0.03em] text-white sm:text-[4.6rem]">
              {c.hero.h1lead} <span className="text-[#4cbfe0]">{c.hero.h1hot}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#9db4d0]">
              {c.hero.body}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => { trackEvent('fde_hero_cta'); scrollToId('start') }} className="btn-hot">{c.hero.ctaPrimary} <LuArrowRight /></button>
              <button onClick={() => scrollToId('engagement')} className="btn-ghost">{c.hero.ctaSecondary}</button>
            </div>
          </Reveal>

          {/* the thesis: the flow, at scale */}
          <Reveal delay={140} className="mt-14 -mx-5 overflow-x-auto px-5 pb-3 md:mx-0 md:overflow-visible md:px-0">
            <svg viewBox="0 0 1000 360" className="w-full" role="img" aria-label={c.heroSvg.aria}>
              <Defs />
              {/* connectors: tangled left -> center */}
              {[70, 120, 170, 220, 270, 300].map((y, i) => (
                <path key={i} id={`hl${i}`} className="flow" strokeWidth="1.4" d={`M232 ${y} C 320 ${y}, 340 ${180 + (i - 3) * 6}, 430 180`} style={{ animationDelay: `${i * 0.2}s` }} />
              ))}
              {/* center -> right */}
              {[120, 160, 200, 240].map((y, i) => (
                <path key={i} id={`hr${i}`} className="flow" strokeWidth="1.6" d={`M600 180 C 660 180, 690 ${y}, 748 ${y}`} style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
              {/* work tokens: complexity pulled in, reliable output emerging */}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <circle key={`tl${i}`} className="tok" r="2.6"><animateMotion dur={`${2.4 + (i % 3) * 0.5}s`} repeatCount="indefinite" begin={`${i * 0.35}s`}><mpath xlinkHref={`#hl${i}`} /></animateMotion></circle>
              ))}
              {[0, 1, 2, 3].map((i) => (
                <circle key={`tr${i}`} className="tok" r="2.9"><animateMotion dur={`${1.9 + (i % 2) * 0.4}s`} repeatCount="indefinite" begin={`${0.8 + i * 0.3}s`}><mpath xlinkHref={`#hr${i}`} /></animateMotion></circle>
              ))}
              <path className="flow" strokeWidth="2" d="M902 180 C 930 180, 940 180, 946 180" />
              {/* left panel */}
              <g>
                <rect x="60" y="52" width="172" height="256" rx="14" fill="url(#fnode)" stroke="rgba(25,162,207,.22)" />
                <text x="146" y="82" textAnchor="middle" fill="#8fb0d8" fontSize="11" fontWeight="700" letterSpacing="1.5">{c.heroSvg.leftTitle1}</text>
                <text x="146" y="98" textAnchor="middle" fill="#8fb0d8" fontSize="11" fontWeight="700" letterSpacing="1.5">{c.heroSvg.leftTitle2}</text>
                {c.heroSvg.leftItems.map((t, i) => (
                  <g key={t}><circle cx="90" cy={132 + i * 33} r="3.5" fill="#19a2cf" /><text x="104" y={136 + i * 33} fill="#9db4d0" fontSize="11">{t}</text></g>
                ))}
              </g>
              {/* center panel (hot) */}
              <g>
                <rect x="430" y="34" width="170" height="292" rx="16" fill="#0a2029" stroke="#19a2cf" strokeWidth="1.5" filter="url(#fg)" opacity="0.96" />
                <text x="515" y="66" textAnchor="middle" fill="#bcd8ff" fontSize="11" fontWeight="700" letterSpacing="1">{c.heroSvg.centerTitle1}</text>
                <text x="515" y="81" textAnchor="middle" fill="#bcd8ff" fontSize="11" fontWeight="700" letterSpacing="1">{c.heroSvg.centerTitle2}</text>
                {c.heroSvg.stages.map((t, i) => (
                  <g key={t}>
                    <rect className="stg-rect" style={{ animationDelay: `${-i * 1.6}s` }} x="452" y={104 + i * 52} width="126" height="40" rx="9" fill="rgba(25,162,207,.08)" stroke="rgba(25,162,207,.3)" />
                    <circle className="stg-dot" style={{ animationDelay: `${-i * 1.6}s` }} cx="474" cy={124 + i * 52} r="5" fill="#2aa7cf" />
                    <text x="490" y={129 + i * 52} fill="#dbe8fb" fontSize="13" fontWeight="600">{t}</text>
                  </g>
                ))}
              </g>
              {/* right nodes */}
              <g>
                {c.heroSvg.rightItems.map((t, i) => (
                  <g key={t}>
                    <rect x="748" y={104 + i * 40} width="150" height="30" rx="8" fill="url(#fnode)" stroke="rgba(25,162,207,.2)" />
                    <circle cx="766" cy={119 + i * 40} r="3.5" fill="#19a2cf" />
                    <text x="778" y={123 + i * 40} fill="#9db4d0" fontSize="11">{t}</text>
                  </g>
                ))}
                <circle cx="946" cy="180" r="30" fill="#0a2029" stroke="#19a2cf" strokeWidth="2" filter="url(#fg)" />
                <path d="M934 180 l8 9 l16 -18" stroke="#4cbfe0" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <text x="146" y="332" textAnchor="middle" fill="#7089b0" fontSize="10" letterSpacing="1">{c.heroSvg.before}</text>
              <text x="515" y="348" textAnchor="middle" fill="#2aa7cf" fontSize="10" fontWeight="700" letterSpacing="1.4">{c.heroSvg.embedded}</text>
              <text x="946" y="332" textAnchor="middle" fill="#4cbfe0" fontSize="10" fontWeight="600" letterSpacing="1">{c.heroSvg.inProduction}</text>
            </svg>
          </Reveal>
        </div>
      </section>

      {/* ── PILOT ────────────────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <H lead={c.pilot.hLead} hot={c.pilot.hHot} />
            <p className="mt-6 text-lg leading-relaxed text-[#9db4d0]">{c.pilot.body}</p>
            <div className="mt-8 space-y-4">
              {c.pilot.items.map(([t, d]) => (
                <div key={t} className="flex gap-4">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-400/80" />
                  <p><span className="font-semibold text-white">{t}.</span> <span className="text-[#8ba3c4]">{d}</span></p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="overflow-x-auto md:overflow-visible">
            <svg viewBox="0 0 460 400" className="w-full" role="img" aria-label={c.pilot.svgAria}>
              <Defs />
              <rect x="20" y="150" width="120" height="96" rx="12" fill="url(#fnode)" stroke="rgba(25,162,207,.3)" />
              <text x="80" y="182" textAnchor="middle" fill="#bcd8ff" fontSize="12" fontWeight="700">{c.pilot.pilotLabel}</text>
              <path d="M60 200 l14 12 l26 -26" stroke="#4cbfe0" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <text x="80" y="234" textAnchor="middle" fill="#8ba3c4" fontSize="10">{c.pilot.pilotSub}</text>
              {[[300, 70], [400, 130], [410, 220], [360, 310], [250, 350], [180, 300]].map(([x, y], i) => (
                <path key={i} className="flow-r" strokeWidth="1.4" d={`M140 198 C 220 ${198 + (i - 3) * 10}, ${x - 60} ${y}, ${x} ${y}`} />
              ))}
              {[[c.pilot.nodes[0], 300, 70], [c.pilot.nodes[1], 400, 130], [c.pilot.nodes[2], 410, 220], [c.pilot.nodes[3], 360, 310], [c.pilot.nodes[4], 250, 350], [c.pilot.nodes[5], 180, 300]].map(([t, x, y]) => (
                <g key={t}>
                  <circle cx={x} cy={y} r="8" fill="#1a1220" stroke="#fb5a6b" strokeWidth="1.3" />
                  <text x={x} y={y - 16} textAnchor="middle" fill="#c99" fontSize="10" fontWeight="600">{t}</text>
                </g>
              ))}
            </svg>
          </Reveal>
        </div>
      </section>

      {/* ── WHAT FDE MEANS ───────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl"><H lead={c.what.hLead} hot={c.what.hHot} /></Reveal>
          <div className="mt-14 grid gap-x-14 gap-y-10 md:grid-cols-3">
            {c.what.blocks.map(([t, d], i) => (
              <Reveal key={t} delay={i * 90}>
                <span className="block h-px w-12 bg-gradient-to-r from-[#19a2cf] to-transparent" />
                <p className="mt-5 font-Quicksand text-xl font-bold text-white">{t}</p>
                <p className="mt-3 leading-relaxed text-[#8ba3c4]">{d}</p>
              </Reveal>
            ))}
          </div>
          {/* discovery -> impact rail */}
          <Reveal delay={120} className="mt-14">
            <div className="hair pt-10">
              <div className="flex flex-wrap items-center justify-between gap-y-6">
                {[LuSearch, LuWorkflow, LuCode, LuTrendingUp].map((I, i, a) => { const [t, d] = c.what.rail[i]; return (
                  <React.Fragment key={t}>
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#19a2cf]/45 bg-[#19a2cf]/[0.07]"><I className="text-[#4cbfe0]" /></span>
                      <div><p className="font-Quicksand font-bold text-white">{t}</p><p className="text-xs text-[#6f88ad]">{d}</p></div>
                    </div>
                    {i < a.length - 1 && <LuArrowRight className="hidden text-[#2d5aa0] md:block" />}
                  </React.Fragment>
                )})}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CODE -> CUSTOMER ─────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl"><H lead={c.code.hLead} hot={c.code.hHot} /><p className="mt-6 text-lg text-[#9db4d0]">{c.code.body}</p></Reveal>
          <Reveal delay={120} className="mt-14">
            <div className="flex flex-wrap items-center justify-center gap-y-8">
              <div className="flex flex-col items-center px-2"><span className="pulse flex h-16 w-16 items-center justify-center rounded-full border border-[#19a2cf]/60 bg-[#0a2029]"><LuCode className="text-2xl text-[#4cbfe0]" /></span><span className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#2aa7cf]">{c.code.codeLabel}</span></div>
              {COMBINE_ICONS.map((I, ci) => { const t = c.code.combine[ci]; return (
                <div key={t} className="flex items-center">
                  <span className="mx-1 h-px w-5 bg-gradient-to-r from-[#19a2cf]/50 to-[#19a2cf]/10" />
                  <div className="flex w-[128px] flex-col items-center gap-2 rounded-xl border border-[#1c3255] bg-[#070f22]/60 px-3 py-5 text-center transition hover:border-[#19a2cf]/45 hover:bg-[#0a1830]">
                    <I className="text-xl text-[#4cbfe0]" /><span className="text-[13px] font-semibold leading-tight text-white">{t}</span>
                  </div>
                </div>
              )})}
              <span className="mx-1 h-px w-5 bg-gradient-to-r from-[#19a2cf]/50 to-[#19a2cf]/10" />
              <div className="flex flex-col items-center px-2"><span className="pulse flex h-16 w-16 items-center justify-center rounded-full border border-[#19a2cf]/60 bg-[#0a2029]"><LuUsers className="text-2xl text-[#4cbfe0]" /></span><span className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#2aa7cf]">{c.code.customerLabel}</span></div>
            </div>
            <p className="mx-auto mt-12 max-w-3xl text-center font-Quicksand text-2xl font-light text-white">{c.code.line1a} <span className="text-[#2aa7cf]">{c.code.line1hot}</span></p>
            <p className="mx-auto mt-3 max-w-xl text-center text-[#8ba3c4]">{c.code.line2}</p>
          </Reveal>
        </div>
      </section>

      {/* ── WHERE WE CREATE VALUE ────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl"><H lead={c.value.hLead} hot={c.value.hHot} /><p className="mt-6 text-lg text-[#9db4d0]">{c.value.body}</p></Reveal>
          <div className="mt-12 grid gap-x-12 sm:grid-cols-2">
            {VALUE_ICONS.map((I, i) => { const t = c.value.items[i]; return (
              <Reveal key={t} delay={(i % 2) * 60}>
                <div className={`flex items-center gap-4 py-5 ${i > 1 ? 'hair' : ''}`}>
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-[#19a2cf]/30 bg-[#19a2cf]/[0.06]"><I className="text-[#4cbfe0]" /></span>
                  <span className="font-medium text-[#dbe8fb]">{t}</span>
                </div>
              </Reveal>
            )})}
          </div>
        </div>
      </section>

      {/* ── BENJAMIN HUB ─────────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <H lead={c.benjamin.hLead} hot={c.benjamin.hHot} />
            <p className="mt-6 text-lg leading-relaxed text-[#9db4d0]">{c.benjamin.p1a}<span className="font-semibold text-[#4cbfe0]">{c.benjamin.p1name}</span>{c.benjamin.p1b}</p>
            <p className="mt-5 leading-relaxed text-[#8ba3c4]">{c.benjamin.p2}</p>
          </Reveal>
          <Reveal delay={120} className="overflow-x-auto md:overflow-visible">
            <svg viewBox="0 0 520 440" className="w-full" role="img" aria-label={c.benjamin.svgAria}>
              <Defs />
              {c.benjamin.items.map((_, i) => {
                const left = i < 4; const row = i % 4
                const sx = left ? 164 : 320; const sy = 71 + row * 100
                return <path key={i} id={`bn${i}`} className="flow" strokeWidth="1.4" d={`M${sx} ${sy} C ${left ? 224 : 260} ${sy}, ${left ? 200 : 320} 220, 260 220`} />
              })}
              {c.benjamin.items.map((_, i) => (
                <circle key={`bt${i}`} className="tok" r="2.4"><animateMotion dur={`${2.6 + (i % 4) * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.3}s`}><mpath xlinkHref={`#bn${i}`} /></animateMotion></circle>
              ))}
              {c.benjamin.items.map((t, i) => {
                const left = i < 4; const row = i % 4
                const x = left ? 6 : 320; const y = 48 + row * 100
                return (
                  <g key={`${t}-${i}`}>
                    <rect x={x} y={y} width="158" height="44" rx="10" fill="url(#fnode)" stroke="rgba(25,162,207,.22)" />
                    <text x={x + 14} y={y + 27} fill="#c3d3e8" fontSize="10.5" fontWeight="600">{t}</text>
                  </g>
                )
              })}
              <circle cx="260" cy="220" r="62" fill="#07181f" stroke="#19a2cf" strokeWidth="2" filter="url(#fg)" />
              <circle cx="260" cy="220" r="74" fill="none" stroke="rgba(25,162,207,.25)" />
              <text x="260" y="214" textAnchor="middle" fill="#ffffff" fontSize="18" fontWeight="700" fontFamily="Quicksand, sans-serif">Maestro</text>
              <text x="260" y="234" textAnchor="middle" fill="#4cbfe0" fontSize="9" fontWeight="700" letterSpacing="0.9">{c.benjamin.core2}</text>
              <text x="260" y="250" textAnchor="middle" fill="#8ba3c4" fontSize="9">{c.benjamin.core3}</text>
            </svg>
          </Reveal>
        </div>
      </section>

      {/* ── ENGAGEMENT RAIL ──────────────────────────────────────────────── */}
      <section id="engagement" className="relative scroll-mt-24 px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl"><H lead={c.engagement.hLead} hot={c.engagement.hHot} /></Reveal>
          <div className="relative mt-16">
            <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-[#19a2cf]/10 via-[#19a2cf]/45 to-[#19a2cf]/10 md:block" />
            <span className="rail-dot hidden md:block" />
            <div className="grid gap-10 md:grid-cols-4">
              {ENGAGEMENT_ICONS.map((I, i) => { const [t, d] = c.engagement.steps[i]; return (
                <Reveal key={t} delay={i * 100}>
                  <div className="relative">
                    <span className="seq relative z-10 flex h-14 w-14 items-center justify-center rounded-full border border-[#19a2cf]/55 bg-[#07181f]" style={{ animationDelay: `${-i * 1.6}s` }}><I className="text-xl text-[#4cbfe0]" /></span>
                    <p className="mt-6 font-Quicksand text-lg font-bold text-white">{t}</p>
                    <p className="mt-2 text-sm leading-relaxed text-[#8ba3c4]">{d}</p>
                  </div>
                </Reveal>
              )})}
            </div>
          </div>
        </div>
      </section>

      {/* ── SENIOR vs TRADITIONAL ────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_0.85fr]">
          <Reveal>
            <H lead={c.ownership.hLead} hot={c.ownership.hHot} />
            <div className="mt-8 space-y-5">
              {c.ownership.points.map((o) => (
                <div key={o} className="flex gap-4"><LuCheck className="mt-1 flex-shrink-0 text-[#2aa7cf]" /><p className="leading-relaxed text-[#a9bfda]">{o}</p></div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col rounded-2xl border border-[#19a2cf]/40 bg-[#07181f]/60 p-6" style={{ boxShadow: '0 18px 60px -28px rgba(25,162,207,.7)' }}>
                <p className="text-center text-[11px] font-bold uppercase tracking-[0.12em] text-[#4cbfe0]">{c.ownership.leanTitle}</p>
                <div className="my-6 flex justify-center"><span className="pulse flex h-14 w-14 items-center justify-center rounded-full border border-[#19a2cf]/55 bg-[#0a2029]"><LuUsers className="text-xl text-[#4cbfe0]" /></span></div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-5">
                  {LEAN_CHIP_ICONS.map((I, li) => { const t = c.ownership.leanChips[li]; return (
                    <div key={t} className="flex flex-col items-center gap-1.5"><I className="text-base text-[#4cbfe0]" /><span className="text-[11px] font-semibold text-[#dbe8fb]">{t}</span></div>
                  )})}
                </div>
                <p className="mt-auto pt-6 text-center text-[11px] font-bold uppercase leading-snug tracking-[0.06em] text-[#4cbfe0]">{c.ownership.leanFoot1}<br />{c.ownership.leanFoot2}</p>
              </div>
              <div className="flex flex-col rounded-2xl border border-white/[0.06] p-6">
                <p className="text-center text-[11px] font-bold uppercase tracking-[0.12em] text-[#7089b0]">{c.ownership.tradTitle}</p>
                <div className="mt-4 divide-y divide-white/[0.05]">
                  {c.ownership.tradRows.map((r) => (<div key={r} className="py-2.5 text-center text-[12px] text-[#8399bb]">{r}</div>))}
                </div>
                <p className="mt-auto pt-4 text-center text-[11px] font-bold uppercase leading-snug tracking-[0.06em] text-rose-400/60">{c.ownership.tradFoot1}<br />{c.ownership.tradFoot2}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── PROOF ────────────────────────────────────────────────────────── */}
      <section className="relative px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-3xl"><H lead={c.proof.hLead} hot={c.proof.hHot} /></Reveal>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-[#173257] bg-[#173257] sm:grid-cols-2 lg:grid-cols-4">
            {c.proof.items.map(([client, big, small, d], i) => (
              <Reveal key={client} delay={i * 80}>
                <div className="lift flex h-full flex-col bg-[#05131a] p-7 hover:bg-[#08202b]">
                  <p className="font-Quicksand text-lg font-bold text-white">{client}</p>
                  <p className="mt-4 font-Quicksand text-4xl font-bold tracking-tight text-[#2aa7cf]"><Metric value={big} /></p>
                  <p className="text-sm text-[#4cbfe0]">{small}</p>
                  <p className="mt-4 text-sm leading-relaxed text-[#8ba3c4]">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section id="start" className="relative scroll-mt-24 px-5 py-20 md:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <H lead={c.cta.hLead} hot={c.cta.hHot} className="!text-center" />
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#9db4d0]">{c.cta.body}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              {PAIN_ICONS.map((I, pi) => { const t = c.cta.pains[pi]; return (<span key={t} className="inline-flex items-center gap-2 rounded-full border border-rose-400/25 bg-rose-400/[0.05] px-4 py-2 text-sm text-rose-100/80"><I className="text-rose-300/70" /> {t}</span>)})}
            </div>
          </Reveal>
          <Reveal delay={120} className="mt-14"><ContactForm f={c.form} /></Reveal>
          <Reveal delay={160}><p className="mt-9 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#2aa7cf]">{c.cta.footerTag}</p><p className="mt-3 text-sm text-[#6f88ad]">{c.cta.orEmail} <a href="mailto:contact@leanit101.com" className="font-semibold text-[#4cbfe0] underline underline-offset-4">contact@leanit101.com</a></p></Reveal>
        </div>
      </section>
    </div>
  )
}

export default ForwardDeployedEngineeringPage
